// src/components/Hero.js

import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

const Hero = () => {
  const canvasRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Particle system for background
  useEffect(() => {
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 60;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.3 - 0.15;
        this.speedY = Math.random() * 0.3 - 0.15;
        this.opacity = Math.random() * 0.4 + 0.1;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(0, 217, 255, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    let animationId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw subtle connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = `rgba(139, 92, 246, ${0.15 - distance / 1000})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [prefersReducedMotion]);

  // Word animation variants
  const wordVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3 + i * 0.1,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };

  return (
    <section
      id="hero"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        paddingTop: '100px',
        paddingBottom: '80px'
      }}
    >
      {/* Background Layer 1: Base Gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, #050816 0%, #0A0D1A 40%, #1A1A2E 100%)',
          zIndex: 0
        }}
      />

      {/* Background Layer 2: Radial Glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 60%)',
          zIndex: 1
        }}
      />

      {/* Background Layer 3: Secondary Glow */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 217, 255, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 1
        }}
      />

      {/* Background Layer 4: Subtle Grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          zIndex: 2,
          opacity: 0.4
        }}
      />

      {/* Background Layer 5: Particles Canvas */}
      {!prefersReducedMotion && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Main Content - Centered */}
      <div
        className="container"
        style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          maxWidth: '900px',
          padding: '0 24px'
        }}
      >
        {/* Eyebrow Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: '32px' }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 24px',
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '9999px',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Sparkles size={16} color="#8B5CF6" />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#A78BFA',
              letterSpacing: '0.05em'
            }}>
              Next-Generation AI Consulting
            </span>
          </span>
        </motion.div>

        {/* Main Headline */}
        <h1
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(42px, 8vw, 80px)',
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            marginBottom: '28px'
          }}
        >
          {prefersReducedMotion ? (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span style={{ color: 'var(--text-primary)' }}>Operational Intelligence</span>
              <br />
              <span style={{ color: 'var(--text-secondary)' }}>for the </span>
              <span className="gradient-text">Modern Enterprise</span>
            </motion.span>
          ) : (
            <>
              <span style={{ display: 'block' }}>
                {['Operational', 'Intelligence'].map((word, i) => (
                  <motion.span
                    key={i}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                    variants={wordVariants}
                    style={{
                      display: 'inline-block',
                      marginRight: '18px',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
              <span style={{ display: 'block', marginTop: '8px' }}>
                <motion.span
                  custom={2}
                  initial="hidden"
                  animate="visible"
                  variants={wordVariants}
                  style={{
                    display: 'inline-block',
                    marginRight: '18px',
                    color: 'var(--text-secondary)'
                  }}
                >
                  for the
                </motion.span>
                <motion.span
                  custom={3}
                  initial="hidden"
                  animate="visible"
                  variants={wordVariants}
                  style={{ display: 'inline-block' }}
                  className="gradient-text"
                >
                  Modern Enterprise
                </motion.span>
              </span>
            </>
          )}
        </h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{
            fontSize: 'clamp(17px, 2.5vw, 20px)',
            lineHeight: 1.7,
            color: 'var(--text-secondary)',
            marginBottom: '48px',
            maxWidth: '720px',
            margin: '0 auto 48px auto'
          }}
        >
          Rosalytics transforms business and manufacturing operations through AI,
          GenAI innovation, advanced analytics, and custom software engineering —
          bridging operational needs and technological possibilities.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '18px 36px',
              background: 'linear-gradient(135deg, #00D9FF 0%, #8B5CF6 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '9999px',
              fontWeight: '600',
              fontSize: '16px',
              boxShadow: '0 10px 40px rgba(0, 217, 255, 0.3)',
              cursor: 'pointer',
              border: 'none'
            }}
          >
            Start a Conversation
            <ArrowRight size={18} />
          </motion.a>

          <motion.a
            href="#capabilities"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#capabilities')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '18px 36px',
              background: 'transparent',
              color: 'var(--text-primary)',
              textDecoration: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '16px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              cursor: 'pointer'
            }}
          >
            Explore Capabilities
          </motion.a>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          style={{
            marginTop: '80px',
            display: 'flex',
            justifyContent: 'center',
            gap: '48px',
            flexWrap: 'wrap'
          }}
        >
          {[
            'Advanced Manufacturing Analytics',
            'GenAI Solutions',
            'Custom Applications'
          ].map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--text-muted)',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: index === 0 ? '#00D9FF' : index === 1 ? '#8B5CF6' : '#FF6B9D'
                }}
              />
              {item}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="scroll-indicator-wrapper"
        style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10
        }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          style={{
            width: '28px',
            height: '44px',
            borderRadius: '14px',
            border: '2px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '10px'
          }}
        >
          <motion.div
            animate={{ y: [0, 14, 0], opacity: [1, 0.3, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            style={{
              width: '4px',
              height: '10px',
              borderRadius: '2px',
              background: 'linear-gradient(180deg, #00D9FF 0%, #8B5CF6 100%)'
            }}
          />
        </motion.div>
      </motion.div>

    </section>
  );
};

export default Hero;
